import { useState } from 'react';
import {
  LayoutGrid,
  Plus,
  Edit2,
  Trash2,
  MoreHorizontal,
  Loader2,
  Star,
  Settings,
  Eye,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { useFloorPlans } from '@/hooks/useFloorPlans';
import { useLocationStore } from '@/store/location';
import { toast } from 'sonner';
import { LocationSelector } from '@/components/location/LocationSelector';
import { FloorPlanEditor } from '@/components/floorplan/FloorPlanEditor';

export default function FloorPlan() {
  const { currentLocation } = useLocationStore();
  const {
    floorPlans,
    defaultFloorPlan,
    isLoading,
    createFloorPlan,
    updateFloorPlan,
    deleteFloorPlan,
  } = useFloorPlans();

  const [activeFloorPlanId, setActiveFloorPlanId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFloorPlanForEdit, setSelectedFloorPlanForEdit] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', isDefault: false });
  const [formError, setFormError] = useState<string | null>(null);

  // Set active floor plan to default when loaded
  const activeFloorPlan = floorPlans.find(fp => fp.id === activeFloorPlanId) || defaultFloorPlan;

  const handleCreateFloorPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Floor plan name is required');
      return;
    }

    try {
      const result = await createFloorPlan.mutateAsync({
        name: formData.name,
        isDefault: formData.isDefault,
      });
      setCreateDialogOpen(false);
      setFormData({ name: '', isDefault: false });
      setActiveFloorPlanId(result.id);
      toast.success('Floor plan created');
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to create floor plan');
    }
  };

  const handleEditFloorPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedFloorPlanForEdit || !formData.name.trim()) {
      setFormError('Floor plan name is required');
      return;
    }

    try {
      await updateFloorPlan.mutateAsync({
        id: selectedFloorPlanForEdit,
        name: formData.name,
        isDefault: formData.isDefault,
      });
      setEditDialogOpen(false);
      setSelectedFloorPlanForEdit(null);
      setFormData({ name: '', isDefault: false });
      toast.success('Floor plan updated');
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to update floor plan');
    }
  };

  const handleDeleteFloorPlan = async (id: string) => {
    const floorPlan = floorPlans.find(fp => fp.id === id);
    if (!confirm(`Delete "${floorPlan?.name}" and all its tables? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteFloorPlan.mutateAsync(id);
      if (activeFloorPlanId === id) {
        setActiveFloorPlanId(null);
      }
      toast.success('Floor plan deleted');
    } catch (err: unknown) {
      toast.error('Failed to delete', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const openEditDialog = (id: string) => {
    const floorPlan = floorPlans.find(fp => fp.id === id);
    if (!floorPlan) return;

    setSelectedFloorPlanForEdit(id);
    setFormData({
      name: floorPlan.name,
      isDefault: floorPlan.is_default,
    });
    setEditDialogOpen(true);
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <LayoutGrid className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Floor Plan
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage tables and seating layouts
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <LocationSelector showAllOption={false} />
        <Button
          variant={isEditMode ? 'default' : 'outline'}
          onClick={() => setIsEditMode(!isEditMode)}
          className="gap-2"
        >
          {isEditMode ? <Eye className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
          {isEditMode ? 'View Mode' : 'Edit Mode'}
        </Button>
        <Button onClick={() => { setFormData({ name: '', isDefault: false }); setCreateDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          New Floor Plan
        </Button>
      </div>
    </div>
  );

  if (!currentLocation) {
    return (
      <DashboardLayout header={header}>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                <LayoutGrid className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <CardTitle>Select a Location</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                Floor plans are location-specific. Please select a location to manage floor plans.
              </p>
              <LocationSelector showAllOption={false} />
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout header={header} fullWidth>
      <div className="flex flex-col h-[calc(100vh-140px)]">
        {/* Floor Plan Tabs */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : floorPlans.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <LayoutGrid className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-1">No floor plans yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a floor plan to start adding tables
              </p>
              <Button onClick={() => { setFormData({ name: '', isDefault: false }); setCreateDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Floor Plan
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
              {floorPlans.map(fp => (
                <Button
                  key={fp.id}
                  variant={activeFloorPlan?.id === fp.id ? 'default' : 'outline'}
                  size="sm"
                  className="shrink-0 gap-2"
                  onClick={() => setActiveFloorPlanId(fp.id)}
                >
                  {fp.is_default && <Star className="h-3 w-3 text-amber-500" />}
                  {fp.name}
                  <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 ml-1">
                    {fp.tables?.length || 0}
                  </Badge>
                  {isEditMode && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 -mr-1"
                        >
                          <MoreHorizontal className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(fp.id)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {!fp.is_default && (
                          <DropdownMenuItem
                            onClick={() => updateFloorPlan.mutate({ id: fp.id, isDefault: true })}
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Set as Default
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteFloorPlan(fp.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </Button>
              ))}
            </div>

            {/* Floor Plan Canvas */}
            {activeFloorPlan && (
              <div className="flex-1">
                <FloorPlanEditor
                  floorPlanId={activeFloorPlan.id}
                  tables={activeFloorPlan.tables || []}
                  isEditMode={isEditMode}
                  onTableSelect={(table) => {
                    if (!isEditMode) {
                      toast.success(`Table ${table.name}`, {
        description: `Capacity: ${table.capacity} | Status: ${table.status}`,
      });
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Floor Plan Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent>
          <form onSubmit={handleCreateFloorPlan}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Create Floor Plan</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Add a new floor plan for {currentLocation.name}
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>
            <ResponsiveModalBody className="space-y-4">
              {formError && (
                <p className="text-sm text-destructive">{formError}</p>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Main Floor, Patio, Upstairs"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isDefault">Set as Default</Label>
                  <p className="text-xs text-muted-foreground">
                    Default floor plan opens automatically
                  </p>
                </div>
                <Switch
                  id="isDefault"
                  checked={formData.isDefault}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, isDefault: checked }))
                  }
                />
              </div>
            </ResponsiveModalBody>
            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createFloorPlan.isPending}>
                {createFloorPlan.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit Floor Plan Dialog */}
      <ResponsiveModal open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <ResponsiveModalContent>
          <form onSubmit={handleEditFloorPlan}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Edit Floor Plan</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Update floor plan settings
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>
            <ResponsiveModalBody className="space-y-4">
              {formError && (
                <p className="text-sm text-destructive">{formError}</p>
              )}
              <div className="space-y-2">
                <Label htmlFor="editName">Name</Label>
                <Input
                  id="editName"
                  placeholder="e.g., Main Floor, Patio, Upstairs"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="editIsDefault">Set as Default</Label>
                  <p className="text-xs text-muted-foreground">
                    Default floor plan opens automatically
                  </p>
                </div>
                <Switch
                  id="editIsDefault"
                  checked={formData.isDefault}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, isDefault: checked }))
                  }
                />
              </div>
            </ResponsiveModalBody>
            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateFloorPlan.isPending}>
                {updateFloorPlan.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </DashboardLayout>
  );
}
